import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, KeyRound, Lock, Monitor, ShieldQuestion, Undo2, X } from 'lucide-react';
import { useDecideApproval, useShareSecret, useWorkspaceAction } from '../../hooks/mutations';
import { toast } from 'sonner';
import { CardShell, ResolvedCard, ghostBtn, inputCls, outcomeOf, placeOf, primaryBtn, secondaryBtn } from './cards/CardShell';
import BotCard from './cards/BotCard';
import CheckinCard from './cards/CheckinCard';
import ConnectCard from './cards/ConnectCard';
import McpServerCard from './cards/McpServerCard';
import QuestionCard from './cards/QuestionCard';
import RecurringTaskCard from './cards/RecurringTaskCard';
import ScheduleCard from './cards/ScheduleCard';
import TakeoverCard from './cards/TakeoverCard';

const CARDS = {
  schedule: ScheduleCard, recurring_task: RecurringTaskCard, question: QuestionCard, takeover: TakeoverCard,
  checkin: CheckinCard, bot: BotCard, connect: ConnectCard, mcp_server: McpServerCard,
};

/** A password for a login: typed into a masked field, never shown to the bot. */
const SecretCard = ({ approval, botName, compact }) => {
  const { t } = useTranslation();
  const decide = useDecideApproval();
  const share = useShareSecret();
  const [secret, setSecret] = useState('');
  const [remember, setRemember] = useState(false);
  if (approval.status !== 'pending') {
    return <ResolvedCard icon={KeyRound} text={approval.summary} ok={approval.status === 'approved'}
      outcome={outcomeOf(approval, t, t('bots.cards.shared'))} />;
  }
  const busy = decide.isPending || share.isPending;
  return (
    <CardShell icon={KeyRound} testid="secret-card" waiting compact={compact}
      eyebrow={t('bots.cards.secret_eyebrow', { bot: botName })} title={approval.summary}
      footer={(
        <>
          <span className="flex-1" />
          <button type="button" className={ghostBtn} disabled={busy}
            onClick={() => decide.mutate({ approvalId: approval.id, approve: false })}>
            {t('bots.secrets.myself')}
          </button>
          <button type="submit" form={`secret-${approval.id}`} className={primaryBtn} disabled={busy || !secret}>
            <Lock className="w-3.5 h-3.5" />{t('bots.secrets.send')}
          </button>
        </>
      )}>
      <form id={`secret-${approval.id}`} className="space-y-2"
        onSubmit={(e) => { e.preventDefault(); if (secret) share.mutate({ approvalId: approval.id, value: secret, remember }); }}>
        {approval.args?.purpose && <p className="text-[13px] text-muted-foreground">{approval.args.purpose}</p>}
        <input type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)}
          placeholder={t('bots.secrets.placeholder')} maxLength={500} className={inputCls} />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-amber-500" />
          {t('bots.secrets.remember')}
        </label>
        <p className="text-[11px] text-muted-foreground">{t('bots.secrets.hint')}</p>
      </form>
    </CardShell>
  );
};

/** The person took the desktop while the bot worked: it waits until it is handed back. */
const HandbackCard = ({ approval, botName, compact }) => {
  const { t } = useTranslation();
  const workspace = useWorkspaceAction();
  if (approval.status !== 'pending') {
    return <ResolvedCard icon={Monitor} text={t('bots.desktop.handback_waiting', { bot: botName })}
      ok={approval.status === 'approved'} outcome={outcomeOf(approval, t, t('bots.cards.handed_back'))} />;
  }
  return (
    <CardShell icon={Monitor} testid="approval-card" waiting compact={compact}
      eyebrow={t('bots.desktop.computer_title')} title={t('bots.desktop.handback_waiting', { bot: botName })}
      footer={(
        <>
          <span className="flex-1" />
          <button type="button" className={primaryBtn} disabled={workspace.isPending}
            onClick={() => workspace.mutate({ projectId: placeOf(approval), action: 'giveBack' }, {
              onError: (err) => toast.error(err?.response?.data?.detail || t('bots.desktop.failed')),
            })}>
            <Undo2 className="w-3.5 h-3.5" />{t('bots.desktop.hand_back')}
          </button>
        </>
      )} />
  );
};

/** A risky action (delete, an app that asks first, a memory change): approve or reject. */
const ActionCard = ({ approval, botName, compact }) => {
  const { t } = useTranslation();
  const decide = useDecideApproval();
  if (approval.status !== 'pending') {
    const ok = approval.status === 'approved';
    return <ResolvedCard icon={ShieldQuestion} text={approval.summary} ok={ok} testid="approval-card-resolved"
      outcome={outcomeOf(approval, t, t('bots.cards.approved'))} />;
  }
  const act = (approve) => decide.mutate({ approvalId: approval.id, approve });
  return (
    <CardShell icon={ShieldQuestion} testid="approval-card" waiting compact={compact}
      eyebrow={t('bots.control.approval_title', { bot: botName })} title={approval.summary}
      footer={(
        <>
          {approval.task_title && compact && <span className="text-xs text-muted-foreground truncate min-w-0 flex-1">{approval.task_title}</span>}
          <span className="flex-1" />
          <button type="button" className={secondaryBtn} disabled={decide.isPending} onClick={() => act(false)}>
            <X className="w-3.5 h-3.5" />{t('bots.control.reject')}
          </button>
          <button type="button" className={primaryBtn} disabled={decide.isPending} onClick={() => act(true)}>
            <Check className="w-3.5 h-3.5" />{t('bots.control.approve')}
          </button>
        </>
      )} />
  );
};

/**
 * Something the bot waits on the person for, as an interactive card in the
 * chat: confirm a schedule or a recurring task, answer a question, take over
 * the computer, share a password or approve a risky action. Decided cards
 * render as one line with their outcome.
 */
const ApprovalCard = ({ approval, botName, compact = false, onOpenTask, onOpenComputer, computerDocked }) => {
  const Kind = CARDS[approval.card?.kind];
  if (Kind) {
    return <Kind approval={approval} botName={botName} compact={compact} onOpenTask={onOpenTask} onOpenComputer={onOpenComputer}
      docked={computerDocked} />;
  }
  if (approval.tool === 'secret_request') return <SecretCard approval={approval} botName={botName} compact={compact} />;
  if (approval.tool === 'desktop_handback') return <HandbackCard approval={approval} botName={botName} compact={compact} />;
  return <ActionCard approval={approval} botName={botName} compact={compact} />;
};

export default ApprovalCard;
